"use client";

import { ArrowRight, Loader, Mail, Sparkles } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CommunityIcon() {
  return <div className={["flex size-[34px] shrink-0", "items-center justify-center", "rounded-full", "border border-[#C8A96A]/55", "bg-[#FFFDF7]", "text-[#073F31]"].join(" ")} aria-hidden="true">
      <Sparkles className="size-[16px]" strokeWidth={1.5} />
    </div>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className={["flex min-h-[44px] w-full", "items-center justify-between gap-4", "rounded-[8px]", "border border-[#DCCFB2]", "bg-white px-4 py-2"].join(" ")}>
        <div className="flex items-center gap-3">
          <Mail className="size-4 shrink-0 text-[#073F31]" strokeWidth={1.6} />

          <div className="text-left">
            <p className="text-[13px] font-semibold text-[#073F31]">
              Welcome to TeeListClub
            </p>

            <p className="mt-0.5 text-[11px] leading-[1.35] text-[#5A6B64]">
              {successMessage}
            </p>
          </div>
        </div>

        <Button type="button" variant="outline" onClick={resetForm} className={["h-8 shrink-0 rounded-[6px]", "border-[#CDBD98]", "bg-transparent px-3", "text-[11px] font-semibold", "text-[#073F31]", "hover:bg-[#F7F4EC]"].join(" ")}>
          Add another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full">
      <div className={["flex w-full overflow-hidden", "rounded-[8px]", "border border-[#D9D2C4]", "bg-white", "shadow-[0_7px_18px_rgba(7,63,49,0.06)]"].join(" ")}>
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="min-w-0 flex-1 space-y-0">
              <FormControl>
                <Input {...field} type="email" autoComplete="email" placeholder="Your email address" className={["h-[44px] w-full", "border-0 bg-transparent", "px-4", "text-[13px] text-[#073F31]", "placeholder:text-[#8A958F]", "focus-visible:ring-0"].join(" ")} />
              </FormControl>
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className={["h-[44px] shrink-0", "rounded-none", "bg-[#BFFF00]", "px-5", "text-[10px] font-bold uppercase", "tracking-[0.06em]", "text-[#073F31]", "shadow-none", "hover:bg-[#CBFF36]", "sm:px-6"].join(" ")}>
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" aria-hidden="true" />
              Joining
            </> : <>
              Join the Club

              <ArrowRight className="ml-3 size-4" strokeWidth={1.8} aria-hidden="true" />
            </>}
        </Button>
      </div>

      <FormMessage message={successMessage} type="success" />

      <FormMessage message={submitError} type="error" />
    </Form>;
}
export default function HomeCommunityCTA() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="home-community-cta" data-home-section="community-cta" aria-label="Join the TeeListClub community" className="w-full border-y border-[#E2D7BE] bg-[#FFFDF7] px-5 py-9 !h-[260px] md:px-8 md:py-10 lg:px-12 lg:py-11" style={{
    fontFamily: '"Raleway", Arial, sans-serif'
  }}>
      <div className="mx-auto w-full max-w-[1480px]">
        <div ref={ref} className="max-w-[1040px] text-center transition-all duration-700 translate-y-0 opacity-100 ![margin:-35px_215.333px_0px_215.333px]">
          <div className="flex justify-center">
            <CommunityIcon />
          </div>

          <p className={["mt-2.5", "text-[9px] font-bold uppercase", "tracking-[0.21em]", "text-[#789134]", "sm:text-[10px]"].join(" ")}>
            Join the TeeListClub Community
          </p>

          <h2 className={["mx-auto mt-2 max-w-[940px]", "text-[29px] font-medium", "leading-[1.05]", "tracking-[-0.03em]", "text-[#073F31]", "sm:text-[33px]", "lg:text-[37px]"].join(" ")} style={{
          fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
        }}>
            Become part of Asia’s new private golf society.
          </h2>

          <p className={["mx-auto mt-2.5 max-w-[820px]", "text-[12px] font-medium", "leading-[1.5]", "text-[#62736C]", "sm:text-[13px]"].join(" ")}>
            Discover exceptional courses across Asia. Receive curated
            destination guides, Golf Passport updates and exclusive founding
            member invitations.
          </p>

          <div className="mx-auto mt-4 max-w-[760px]">
            <FormProvider formId="newsletter-signup-premium" sectionName="NewsletterSignup" defaultValues={{
            email: ""
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            }
          }} submitText="Join the Club" successMessage="You’re now part of the TeeListClub community.">
              <NewsletterForm />
            </FormProvider>
          </div>

          <p className="mt-2 text-[10px] font-medium text-[#7A8781]">
            Free to join. No spam. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>;
}